import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { createElement, useCallback, useMemo, useState } from "react";
import { TbDotsVertical } from "react-icons/tb";

import {
    DepartureProcessAction,
    DepartureProcessItem,
    buildDepartureProcessItem,
} from "@/pages/HotelPlanning/departure/utils/process";
import { IHotelPlanningDepartureResponseItem, getHotelDepartureRowId } from "@/schemas/hotelPlanningDepartureSchema";
import {
    useHotelPlanningDepartureActions,
    useHotelPlanningDepartureSelectedRows,
} from "@/stores/use-hotel-planning-departure-table";

import { HotelDepartureClickActionNames, hotelDepartureActions } from "./HotelDepartureActionsColumn";

function HotelDepartureActionsHeader({ allRows }: { allRows: IHotelPlanningDepartureResponseItem[] }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { updateShowHotelDepartureProgressDialog } = useHotelPlanningDepartureActions();
    const open = Boolean(anchorEl);
    const selectedRowIds = useHotelPlanningDepartureSelectedRows();
    const selectedRows = useMemo(
        () => allRows.filter((row) => selectedRowIds.includes(getHotelDepartureRowId(row))),
        [allRows, selectedRowIds]
    );

    // Processable items among the selection, per action (drops rows with no driver / wrong status).
    // The action map lives inside the memo: ActionsColumn <-> ActionsHeader import each other, so
    // reading the enum at module top-level would hit it before ActionsColumn finishes initializing.
    const itemsByAction = useMemo(() => {
        const actionByName: Record<HotelDepartureClickActionNames, DepartureProcessAction> = {
            [HotelDepartureClickActionNames.START_TRANSFER]: "start",
            [HotelDepartureClickActionNames.COMPLETE_TRANSFER]: "complete",
        };
        const buckets: Record<HotelDepartureClickActionNames, DepartureProcessItem[]> = {
            [HotelDepartureClickActionNames.START_TRANSFER]: [],
            [HotelDepartureClickActionNames.COMPLETE_TRANSFER]: [],
        };
        for (const row of selectedRows) {
            for (const name of Object.values(HotelDepartureClickActionNames)) {
                const item = buildDepartureProcessItem(row, actionByName[name]);
                if (item) buckets[name].push(item);
            }
        }
        return buckets;
    }, [selectedRows]);

    const handleTransferProcess = useCallback(
        (actionName: HotelDepartureClickActionNames) => {
            const items = itemsByAction[actionName];
            if (!items.length) return;
            updateShowHotelDepartureProgressDialog(items);
        },
        [itemsByAction, updateShowHotelDepartureProgressDialog]
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div className="flex items-center  justify-center !mx-auto">
            {hotelDepartureActions.length > 0 && (
                <>
                    <IconButton onClick={handleClick} size="small" className=" !w-[30px] grow-0 shrink-0 !h-[30px]">
                        <TbDotsVertical size={18} />
                    </IconButton>
                    <Menu
                        sx={{
                            ":root": {},
                        }}
                        classes={{
                            paper: "!min-w-[178.58px]",
                            list: "!min-w-[178.58px]",
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                    >
                        {hotelDepartureActions.map((action) => {
                            const count = itemsByAction[action.onClick].length;
                            return (
                                <MenuItem
                                    disabled={!count}
                                    key={action.label}
                                    onClick={() => handleTransferProcess(action.onClick)}
                                    sx={{}}
                                >
                                    <ListItemIcon>{createElement(action.icon, { color: action.color })}</ListItemIcon>
                                    <ListItemText>{action.label}</ListItemText>
                                    <Typography variant="body2" sx={{ color: "text.secondary", paddingLeft: 3 }}>
                                        {count ? count.toString() : "0"}
                                    </Typography>
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </>
            )}
        </div>
    );
}

export default HotelDepartureActionsHeader;
